import { Button } from '@heidi/design-system/button';
import { Link } from '@heidi/design-system/link';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ChevronDown, ChevronUp, ExternalLink, ImageOff } from 'lucide-react';
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { VisualDxDetailResult } from '../../contracts';

const messages = defineMessages({
  imageAlt: {
    id: 'jDEOrFNiG3',
    defaultMessage: 'VisualDx clinical image for {name}',
  },
  showMore: {
    id: 'aWpBzjCXKS',
    defaultMessage: 'Show more',
  },
  showLess: {
    id: 'qyJtWyZ0yt',
    defaultMessage: 'Show less',
  },
  viewFullDetails: {
    id: 'SVrbvRx3gS',
    defaultMessage: 'View full details on VisualDx',
  },
});

interface VisualDxDetailPaneProps {
  detail: VisualDxDetailResult;
  className?: string;
}

/**
 * Collapsible detail pane for a completed ``get_diagnostic_info`` result.
 *
 * Shows the diagnosis name, synopsis (truncated when collapsed), the first
 * clinical image, and a link to the full VisualDx page. Expanding reveals the
 * full synopsis text and all available images.
 */
export const VisualDxDetailPane = ({
  detail,
  className,
}: VisualDxDetailPaneProps): JSX.Element | null => {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

  // Nothing meaningful to render if name and synopsis are both absent
  if (!detail.name && !detail.synopsis && detail.image_urls.length === 0) {
    return null;
  }

  const visibleImages = isExpanded ? detail.image_urls : detail.image_urls.slice(0, 1);

  const hasSynopsis = Boolean(detail.synopsis);
  const hasExpandableContent = hasSynopsis || detail.image_urls.length > 1;

  const handleImageError = (url: string) => {
    setImageErrors((prev) => ({ ...prev, [url]: true }));
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-lg border border-border bg-surface p-3',
        className
      )}
    >
      {/* Primary image */}
      {visibleImages.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {visibleImages.map((url) => (
            <div
              key={url}
              className="relative h-36 w-full overflow-hidden rounded-md bg-background-secondary sm:w-auto sm:flex-1"
            >
              {imageErrors[url] ? (
                <div className="flex size-full items-center justify-center">
                  <ImageOff className="size-6 text-text-tertiary" />
                </div>
              ) : (
                // VisualDx CDN URLs are token-specific, so they cannot be
                // allowlisted for next/image.
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={url}
                  alt={intl.formatMessage(messages.imageAlt, { name: detail.name })}
                  className="size-full object-cover"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  onError={() => handleImageError(url)}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Synopsis */}
      {hasSynopsis && (
        <TypographyP2
          className={cn('text-text-secondary', !isExpanded && 'line-clamp-3')}
        >
          {detail.synopsis}
        </TypographyP2>
      )}

      {/* Footer: expand toggle + external link */}
      <div className="flex items-center justify-between gap-2">
        {hasExpandableContent && (
          <Button
            variant="ghost"
            size="sm"
            flush="start"
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            <TypographyCaption className="text-text-link hover:underline">
              {intl.formatMessage(isExpanded ? messages.showLess : messages.showMore)}
            </TypographyCaption>
            {isExpanded ? (
              <ChevronUp className="size-3.5 text-text-link" />
            ) : (
              <ChevronDown className="size-3.5 text-text-link" />
            )}
          </Button>
        )}

        {detail.visualdx_url && (
          <Link
            href={detail.visualdx_url}
            target="_blank"
            rel="noopener noreferrer"
            variant="inline"
            className="ms-auto inline-flex items-center gap-1"
          >
            <TypographyCaption className="text-text-link hover:underline">
              {intl.formatMessage(messages.viewFullDetails)}
            </TypographyCaption>
            <ExternalLink className="size-3 shrink-0 text-text-link" />
          </Link>
        )}
      </div>
    </div>
  );
};
